import type {
    MarkdownVariantsType,
    MarkdownInlineTypes,
    TextMarkdownTypes,
    MarkdownKeys
} from "./type";

export const DEFAULT_VARIANTS: MarkdownVariantsType = {
    "# ": "headlineMediumSize",
    "### ": "titleMediumSize",
    "<br/>": "bodyLargeSize",
    "## ": "titleLargeSize",
    "```": "monoMediumSize",
    "<p>": "bodyLargeSize",
    "1. ": "bodyLargeSize",
    "- ": "bodyLargeSize",
    "* ": "bodyLargeSize",
    "`": "monoMediumSize",
    "+ ": "bodyLargeSize",
    "<<": "bodyLargeSize",
    "> ": "bodyLargeSize",
    "": "bodyLargeSize"
};

export const READING_VARIANTS: MarkdownVariantsType = {
    "<br/>": "readingMediumSize",
    "<p>": "readingMediumSize",
    "1. ": "readingMediumSize",
    "- ": "readingMediumSize",
    "* ": "readingMediumSize",
    "+ ": "readingMediumSize",
    "<<": "readingMediumSize",
    "> ": "readingMediumSize",
    "": "readingMediumSize"
};

export const MINOR_HEADING_VARIANTS: MarkdownVariantsType = {
    "###### ": "titleSmallSize",
    "##### ": "titleSmallSize",
    "#### ": "titleSmallSize"
};

export const MARKDOWN_VARIANT_ALIASES: Partial<Record<MarkdownKeys, MarkdownKeys>> = {
    "1. ": "- ",
    "+ ": "- ",
    "> ": "<<",
    "<<": "> "
};

export const MARKDOWN_HEADING_KEYS: Array<TextMarkdownTypes> = [
    "# ",
    "## ",
    "### ",
    "#### ",
    "##### ",
    "###### "
];

export const MARKDOWN_STRONG_VARIANT: keyof NCoreUIKit.Typography = "titleMediumSize";

export const MARKDOWN_FALLBACK_VARIANT: keyof NCoreUIKit.Typography = "bodyLargeSize";

export const MARKDOWN_CODE_VARIANT: keyof NCoreUIKit.Typography = "monoMediumSize";

export const MARKDOWN_READING_STRONG_WEIGHT = 600;

export const MARKDOWN_READING_REGULAR_WEIGHT = 400;

export const MARKDOWN_LIST_MARKERS: Array<string> = [
    "•",
    "◦",
    "▪"
];

export const MARKDOWN_HTML_INLINE_TAGS: Record<string, MarkdownInlineTypes> = {
    strike: "strikethrough",
    del: "strikethrough",
    s: "strikethrough",
    ins: "underline",
    strong: "strong",
    em: "emphasis",
    u: "underline",
    i: "emphasis",
    b: "strong"
};

export const MARKDOWN_HTML_ENTITIES: Record<string, string> = {
    nbsp: "\u00A0",
    quot: "\"",
    apos: "'",
    amp: "&",
    lt: "<",
    gt: ">"
};

export const MARKDOWN_LINK_HREF_ATTRS = {
    rel: "noopener noreferrer",
    target: "_blank"
};

export const MARKDOWN_ESCAPABLE_CHARACTERS = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";

export const MARKDOWN_PUNCTUATION_PATTERN = /[!-/:-@[-`{-~\u00A1\u00A7\u00AB\u00B6\u00B7\u00BB\u00BF\u2010-\u2027\u2030-\u205E\u3001-\u3003\u3008-\u3011]/;

export const MARKDOWN_WHITESPACE_PATTERN = /\s/;

export const MARKDOWN_URL_BOUNDARY_CHARACTERS = "*_~(";

export const MARKDOWN_ATX_HEADING_PATTERN = /^ {0,3}(#{1,6})(?:[ \t]|$)/;

export const MARKDOWN_THEMATIC_BREAK_PATTERN = /^ {0,3}(?:(?:\*[ \t]*){3,}|(?:-[ \t]*){3,}|(?:_[ \t]*){3,})$/;

export const MARKDOWN_SETEXT_UNDERLINE_PATTERN = /^ {0,3}(=+|-+)[ \t]*$/;

export const MARKDOWN_FENCE_PATTERN = /^([ \t]*)(`{3,}|~{3,})(.*)$/;

export const MARKDOWN_BLOCKQUOTE_PATTERN = /^ {0,3}>[ \t]?/;

export const MARKDOWN_LIST_ITEM_PATTERN = /^([ \t]*)(?:([-*+])|(\d{1,9})([.)]))(?:[ \t]+(.*)|[ \t]*)$/;

export const MARKDOWN_BREAK_LINE_PATTERN = /^[ \t]*<br[ \t]*\/?>[ \t]*$/i;

export const MARKDOWN_BREAK_TAG_PATTERN = /^<br[ \t]*\/?>/i;

export const MARKDOWN_HTML_TAG_PATTERN = /^<(strike|strong|del|ins|em|s|u|i|b)>/i;

export const MARKDOWN_AUTOLINK_PATTERN = /^<([A-Za-z][A-Za-z0-9+.-]{1,31}:[^\s<>]*)>/;

export const MARKDOWN_EMAIL_AUTOLINK_PATTERN = /^<([A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*)>/;

export const MARKDOWN_BARE_URL_PATTERN = /^https?:\/\/[^\s<]+/i;

export const MARKDOWN_BARE_URL_TRAILING_CHARACTERS = "?!.,:;*_~'\"";

export const MARKDOWN_ENTITY_PATTERN = /^&(#[0-9]{1,7}|#[xX][0-9a-fA-F]{1,6}|[A-Za-z][A-Za-z0-9]{1,31});/;

export const MARKDOWN_UNSAFE_URL_PATTERN = /^(?:javascript|vbscript|file|data):/;

export const MARKDOWN_SAFE_IMAGE_DATA_PATTERN = /^data:image\/(?:gif|png|jpeg|webp);/;

export const MARKDOWN_URL_TAB_OR_NEWLINE_PATTERN = /[\t\n\r]/g;

export const MARKDOWN_URL_SPACE_CODE = 32;

export const MARKDOWN_MAX_NESTING = 20;

export const MARKDOWN_MAX_LINK_DESTINATION_LENGTH = 8192;

export const MARKDOWN_MAX_LINK_LABEL_LENGTH = 4096;

export const MARKDOWN_PARAGRAPH_TAG_PATTERN = /^<p>|<\/p>$/g;

export const LEGACY_IMAGE_PATTERN = /^\[[^\]]*\]\([^)]*\)(?:\s*<[^>]*>)?(?:\s*--[A-Za-z]+--)?\s*$/;

export const LEGACY_INLINE_PATTERN = /(\*\*.*?\*\*|__.*?__)/g;
